import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Plus,
  Search,
  Lightbulb,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  AlertCircle,
  ArrowRight,
  FileText,
} from 'lucide-react';

import {
  Card,
  CardContent,
  Button,
  Input,
  Select,
  Badge,
  EmptyState,
  Pagination,
  Spinner,
} from '../components/ui';
import { titlesApi } from '../lib/api';
import { formatRelativeTime } from '../lib/utils';
import type { TitleRunListItem } from '../types';

const PAGE_SIZE = 10;

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'available', label: 'Has unused titles' },
  { value: 'in_progress', label: 'Generating' },
  { value: 'failed', label: 'Failed' },
];

const statusMeta: Record<
  string,
  { label: string; variant: 'success' | 'info' | 'warning' | 'danger'; icon: React.ReactNode }
> = {
  completed: {
    label: 'Ready',
    variant: 'success',
    icon: <CheckCircle className="h-3 w-3" />,
  },
  processing: {
    label: 'Generating',
    variant: 'info',
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
  },
  pending: {
    label: 'Queued',
    variant: 'warning',
    icon: <Clock className="h-3 w-3" />,
  },
  failed: {
    label: 'Failed',
    variant: 'danger',
    icon: <XCircle className="h-3 w-3" />,
  },
};

/**
 * History of every title generation run.
 *
 * A run is a pool of titles drawn from over time — write a few now, come back
 * for the rest later. Without this page a run is only reachable while its id
 * is in the URL, so leaving /generate would strand titles that are still
 * stored and still usable.
 */
export const TitleRunList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['title-runs', currentPage],
    queryFn: () => titlesApi.getRuns(currentPage, PAGE_SIZE),
    // Runs generate in the background, so a list left open should keep up.
    refetchInterval: 15000,
    staleTime: 0,
  });

  const runs = data?.items ?? [];
  const totalPages = data?.total_pages ?? 1;
  const total = data?.total ?? 0;

  const remainingOf = (r: TitleRunListItem) =>
    Math.max(r.title_count - r.used_count, 0);

  // Client-side filter over the current page, matching JobSessionList.
  const filtered = runs.filter((r) => {
    const matchSearch =
      search === '' ||
      r.keywords.some((k) => k.toLowerCase().includes(search.toLowerCase())) ||
      r.created_by.toLowerCase().includes(search.toLowerCase());

    const matchStatus =
      statusFilter === 'all' ||
      (statusFilter === 'available' &&
        r.status === 'completed' &&
        remainingOf(r) > 0) ||
      (statusFilter === 'failed' && r.status === 'failed') ||
      (statusFilter === 'in_progress' &&
        (r.status === 'pending' || r.status === 'processing'));

    return matchSearch && matchStatus;
  });

  const availableRuns = runs.filter(
    (r) => r.status === 'completed' && remainingOf(r) > 0
  ).length;
  const unusedTitles = runs.reduce((sum, r) => sum + remainingOf(r), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={<AlertCircle className="h-12 w-12 text-red-400" />}
        title="Could not load title runs"
        description="Something went wrong fetching your generated titles. Try again shortly."
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Generated Titles
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {total} run{total !== 1 ? 's' : ''} total · {unusedTitles} title
            {unusedTitles !== 1 ? 's' : ''} still available on this page
          </p>
        </div>
        <Link to="/generate">
          <Button leftIcon={<Plus className="h-4 w-4" />}>New Generation</Button>
        </Link>
      </div>

      {/* ── Stats Strip ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          {
            label: 'Total Runs',
            value: total,
            icon: <Lightbulb className="h-5 w-5 text-primary-600" />,
            color: 'bg-primary-100 dark:bg-primary-900/30',
          },
          {
            label: 'Runs With Titles Left',
            value: availableRuns,
            icon: <CheckCircle className="h-5 w-5 text-green-600" />,
            color: 'bg-green-100 dark:bg-green-900/30',
          },
          {
            label: 'Unused Titles',
            value: unusedTitles,
            icon: <FileText className="h-5 w-5 text-yellow-600" />,
            color: 'bg-yellow-100 dark:bg-yellow-900/30',
          },
        ].map((stat) => (
          <Card key={stat.label} className="relative overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stat.color}`}>{stat.icon}</div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stat.value}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by keyword or creator"
          leftIcon={<Search className="h-5 w-5" />}
        />
        <div className="sm:w-56">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={statusOptions}
          />
        </div>
      </div>

      {/* ── List ── */}
      {filtered.length === 0 ? (
        <EmptyState
          icon={<Lightbulb className="h-12 w-12 text-gray-400" />}
          title={runs.length === 0 ? 'No title runs yet' : 'No runs match your filters'}
          description={
            runs.length === 0
              ? 'Generate titles from keywords and they will be saved here for you to come back to.'
              : 'Try a different search term or status filter.'
          }
          action={
            runs.length === 0
              ? { label: 'Generate Titles', onClick: () => navigate('/generate') }
              : undefined
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((run: TitleRunListItem, i: number) => {
            const meta = statusMeta[run.status] ?? statusMeta.pending;
            const remaining = remainingOf(run);
            const isExhausted = run.status === 'completed' && remaining === 0;

            return (
              <motion.div
                key={run.run_id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.3) }}
              >
                <Card
                  hover
                  onClick={() => navigate(`/generate?run=${run.run_id}`)}
                  className={`cursor-pointer ${isExhausted ? 'opacity-70' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={meta.variant} size="sm">
                            <span className="inline-flex items-center gap-1">
                              {meta.icon}
                              {meta.label}
                            </span>
                          </Badge>
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {formatRelativeTime(run.created_at)}
                          </span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            · {run.created_by}
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {run.keywords.map((kw) => (
                            <Badge key={kw} variant="primary" size="sm">
                              {kw}
                            </Badge>
                          ))}
                        </div>

                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {run.title_count} title{run.title_count !== 1 ? 's' : ''}
                          {run.used_count > 0 && <> · {run.used_count} written</>}
                          {/* The number that decides whether this run is worth reopening */}
                          {remaining > 0 && (
                            <span className="text-primary-600 dark:text-primary-400 font-medium">
                              {' '}
                              · {remaining} available
                            </span>
                          )}
                          {isExhausted && <> · all written</>}
                          {run.generation_count > 1 && (
                            <> · {run.generation_count} batches</>
                          )}
                        </p>
                      </div>

                      <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          total={total}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};
